import React, {useEffect, useState} from 'react'
import GraphViewWrapper from "./components/GraphViewWrapper";
import {prepareNodes} from "./services/NodeService";
import Node from './components/Node'
import {computeEdges} from "./services/EdgeService";
import Edge from "./components/Edge";
import './styles.css'
import {GraphRelationProvider} from "./services/ButtonRelationService";
import ButtonConnectorLine from "./components/ButtonConnectorLine";
import ContextMenu from "../../../../../../components-new/ui/ContextMenu";
import Translate from "../../../../../../components/Translate";
import Colors from "../../../../../../utils/Colors";
import ScanIcon from "../../../../../../assets/icons/ScanIcon";
import DeleteIcon from "../../../../../../assets/icons/DeleteIcon";

const GraphView = ({
                       nodesData,
                       edgesData,
                       rootNodeId,
                       selectNode,
                       addNode,
                       connectButtonToDestination,
                       removeButtonConnection,
                       onDeleteNode,
                       editNode,
                       isFetching,
                   }) => {

    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [highlightedEdges, setHighlightedEdges] = useState([]);
    const [centralizeContainer, setCentralizeContainer] = useState(null);
    const [isEdgeFirstRender, setIsEdgeFirstRender] = useState(true);
    const [isNodesFirstRender, setIsNodesFirstRender] = useState(true);

    const [showContextMenu, setShowContextMenu] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState({x: 0, y: 0});
    const [contextMenuTarget, setContextMenuTarget] = useState(null); // target: 'button' | 'node'

    useEffect(() => {
        if (!nodesData.length) return;

        const root = nodesData.find(node => node.id === rootNodeId)

        if (root) root.isRoot = true

        setNodes(prepareNodes(nodesData, edgesData));
        if (isNodesFirstRender) {
            setCentralizeContainer({value: 'scale', count: 1});
            setIsNodesFirstRender(false);
        } else {
            setCentralizeContainer(prev => ({value: 'combine', count: prev.count + 1}));
        }

    }, [nodesData, edgesData])

    useEffect(() => {
        const timeout = requestAnimationFrame(() => {
            const computedEdges = computeEdges(nodes, edgesData)

            if (isEdgeFirstRender) {
                setTimeout(() => {
                    setEdges(computedEdges);
                }, 1000)
                setIsEdgeFirstRender(false);
            } else {
                setEdges(computedEdges);
            }
        });
        return () => cancelAnimationFrame(timeout);
    }, [nodes]);

    function handleContextMenu(e) {
        e.preventDefault();
        e.stopPropagation();
        setContextMenuPosition({x: e.pageX + 5, y: e.pageY + 5}); //without these pixels it understands as drag because mouseOut event works on already opened context menu
        setShowContextMenu(true);
    }

    return (
        <div className="graph-view-autosizer">
            <GraphRelationProvider>
                <GraphViewWrapper
                    centralize={centralizeContainer}
                    onContextMenu={(e) => {
                        setContextMenuTarget(null)
                        handleContextMenu(e)
                    }}
                >
                    {
                        ({gTransform, gRef}) => {
                            return (
                                <React.Fragment>
                                    {
                                        nodes.map(node => (
                                            <Node
                                                key={node.id}
                                                node={node}
                                                isHighlighted={highlightedEdges.some(edge => edge.nodeId === node.id)}
                                                connectButtonToDestination={connectButtonToDestination}
                                                onNodeContextMenu={(event, node) => {
                                                    setContextMenuTarget({node, type: 'node'});
                                                    handleContextMenu(event);
                                                }}
                                                onButtonContextMenu={(event,node,button) => {
                                                    setContextMenuTarget({node, button, type: 'button'});
                                                    handleContextMenu(event);
                                                }}
                                                onSelect={(flags) => selectNode(node, flags)}
                                                onAddButton={() => selectNode(node, ['addButton'])}
                                                onCreateNewNode={(btnId) => addNode(btnId)}
                                                onHighlightButtonEdge={(btnId) => {
                                                    const edge = edges.find(edge => edge.buttonId === btnId)
                                                    if (edge && !highlightedEdges.some(he => he.buttonId === edge.buttonId)) {
                                                        setHighlightedEdges([...highlightedEdges, edge])
                                                    }
                                                }
                                                }
                                                onUnHighlightButtonEdge={(btnId) => setHighlightedEdges(highlightedEdges.filter(edge => edge.buttonId !== btnId))}
                                                onHighlightNodeEdges={(nodeId) => setHighlightedEdges(edges.filter(edge => edge.nodeId === nodeId))}
                                                onUnHighlightNodeEdges={(nodeId) => setHighlightedEdges(highlightedEdges.filter(edge => edge.nodeId !== nodeId))}
                                            />
                                        ))
                                    }
                                    {
                                        edges.map(edge => (
                                            <Edge
                                                key={edge.buttonId + '-' + edge.nodeId}
                                                edge={edge}
                                            />
                                        ))
                                    }
                                    {
                                        highlightedEdges.map(edge => (
                                            <Edge
                                                key={edge.buttonId + '-' + edge.nodeId}
                                                edge={edge}
                                                isHighlightEdge={true}
                                                isHighlighted={true}
                                            />
                                        ))
                                    }
                                    {
                                        <ButtonConnectorLine
                                            edges={edges}
                                            gRef={gRef}
                                            removeButtonConnection={removeButtonConnection}
                                        />
                                    }
                                </React.Fragment>
                            )
                        }
                    }
                </GraphViewWrapper>
            </GraphRelationProvider>
            {showContextMenu &&
                <ContextMenu
                    position={contextMenuPosition}
                    onClose={() => setShowContextMenu(false)}
                    items={[
                        {
                            key: 'centralize',
                            label: <Translate text="centralizeTree"/>,
                            icon: <ScanIcon strokeWidth={1.5} height={20} width={20} style={{color: Colors.blueDark}} stroke="currentColor"/>,
                            onClick: () => {
                                setCentralizeContainer(prev => ({value: 'combine', count: prev ? prev.count + 1 : 1}));
                                setShowContextMenu(false);
                            },
                        },
                        ...((contextMenuTarget && contextMenuTarget.type === 'node') ?
                               [ {
                                    key: 'deleteNode',
                                    danger: true,
                                    label: <Translate text="deleteNode"/>,
                                    icon: <DeleteIcon strokeWidth={1.5} height={20} width={20} style={{color: Colors.blueDark}} stroke="currentColor"/>,
                                    disabled:isFetching,
                                    onClick: () => {
                                        contextMenuTarget.node && onDeleteNode(contextMenuTarget.node);
                                        setContextMenuTarget(null);
                                        setShowContextMenu(false);
                                    },
                                }]
                                : []

                        ),
                        ...((contextMenuTarget && contextMenuTarget.type === 'button') ?
                                [ {
                                    key: 'deleteButton',
                                    danger: true,
                                    label: <Translate text="deleteButton"/>,
                                    icon: <DeleteIcon strokeWidth={1.5} height={20} width={20} style={{color: Colors.blueDark}} stroke="currentColor"/>,
                                    disabled:isFetching,
                                    onClick: () => {
                                        if(contextMenuTarget.node && contextMenuTarget.node && contextMenuTarget.button) {
                                            editNode({
                                                ...contextMenuTarget.node.data,
                                                buttons: contextMenuTarget.node.data.buttons.filter(b => b.id !== contextMenuTarget.button.id)
                                            })
                                        }
                                        setContextMenuTarget(null);
                                        setShowContextMenu(false);
                                    }
                                }]
                                : []

                        )
                    ]}
                />
            }
        </div>
    )
}

export default GraphView;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/graph/containers/GraphView/index.jsx